import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { useOnlineStatusContext } from '../context/OnlineStatusContext';

interface NetworkStatusProps {
  // Czy pokazywać tylko gdy offline lub słabe połączenie
  showOnlyWhenIssues?: boolean;
  // Czy pokazywać ostrzeżenie przy słabym połączeniu
  showPoorConnectionWarning?: boolean;
}

const NetworkStatus = ({ 
  showOnlyWhenIssues = true,
  showPoorConnectionWarning = true 
}: NetworkStatusProps): JSX.Element | null => {
  const { isOnline, connectionQuality } = useOnlineStatusContext();
  const [showNotification, setShowNotification] = useState(false);
  const [dismissedPoorConnection, setDismissedPoorConnection] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setShowNotification(true);
      setDismissedPoorConnection(false);
      setIsExiting(false);
    } else if (connectionQuality === 'poor' && !dismissedPoorConnection && showPoorConnectionWarning) {
      setShowNotification(true);
      setIsExiting(false);
    } else if (connectionQuality === 'good') {
      // Pokaż krótką informację o powrocie połączenia
      setShowNotification(true);
      setIsExiting(false);
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          setShowNotification(false);
        }, 300);
      }, 3000);
      return (): void => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [isOnline, connectionQuality, dismissedPoorConnection, showPoorConnectionWarning]);

  const handleDismissPoorConnection = (): void => {
    setDismissedPoorConnection(true);
    setIsExiting(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 300);
  };

  if (showOnlyWhenIssues && isOnline && connectionQuality === 'good') {
    return null;
  }

  if (!showNotification) return null;

  const baseClasses = "fixed top-0 left-0 right-0 z-50 flex items-center gap-3 px-4 py-3 shadow-lg transition-all duration-300";
  const animationClasses = isExiting 
    ? "-translate-y-full opacity-0" 
    : "translate-y-0 opacity-100";

  // Offline state
  // if (!isOnline) {
  //   return (
  //     <div 
  //       className={`${baseClasses} ${animationClasses} bg-red-50/90 border-b-2 border-red-500 text-red-900`}
  //       role="alert"
  //       aria-live="polite"
  //     >
  //       <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0 animate-pulse" />
  //       <div className="flex-1">
  //         <p className="font-semibold text-sm">Tryb offline</p>
  //         <p className="text-xs mt-0.5">
  //           Nadal możesz korzystać z pobranych materiałów.{' '}
  //           <Link 
  //             to="/offline-videos" 
  //             className="underline hover:text-red-700 font-medium"
  //           >
  //             Zobacz pobrane filmy
  //           </Link>
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }
  
  // Poor connection state
  if (isOnline && connectionQuality === 'poor' && showPoorConnectionWarning) {
    return (
      <div 
        className={`${baseClasses} ${animationClasses} bg-amber-50/90 border-b-2 border-amber-500 text-amber-900`}
        role="alert"
        aria-live="polite"
      >
        <div className="w-3 h-3 rounded-full bg-amber-500/70 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-semibold text-sm">Słabe połączenie internetowe</p>
          <p className="text-xs mt-0.5">
            Niektóre funkcje mogą działać wolniej lub nie działać w ogóle.
          </p>
        </div>
        <button 
          onClick={handleDismissPoorConnection}
          className="p-2 hover:bg-amber-100 rounded-full transition-colors flex-shrink-0"
          aria-label="Zamknij powiadomienie"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    );
  }
  
  // Good connection restored
  if (isOnline && connectionQuality === 'good') {
    return (
      <div 
        className={`${baseClasses} ${animationClasses} bg-green-50/90 border-b-2 border-green-500 text-green-900`}
        role="alert"
        aria-live="polite"
      >
        <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0" />
        <p className="font-semibold text-sm">Połączenie przywrócone</p>
      </div>
    );
  }

  return null;
};

export default NetworkStatus; 